import {
  QConfigGeneratorGroupLevel,
  QConfigQuestionType,
  QuestionTagCategory,
  useCreateQConfigGeneratorMutation,
  useGetTagsByCategoryQuery,
} from '@exam-management/graphql';
import { useNavigate, useParams } from '@exam-management/router';
import { Form } from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { zodResolver, type PropsWithChildren } from '@shared/utils';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import {
  QConfigGeneratorCreationFormSchema,
  type QConfigGeneratorCreationFormInput,
} from './schema';
interface QConfigGeneratorCreationFormProviderProps extends PropsWithChildren {
  setErrorMessage: (value: string | null) => void;
}

export default function QConfigGeneratorCreationFormProvider(
  props: Readonly<QConfigGeneratorCreationFormProviderProps>,
) {
  const { setErrorMessage, children } = props;
  const navigate = useNavigate();
  const { courseId } = useParams('/exam-courses/:courseId/qconfig-generators/create');
  const { showSnackbar } = useSnackbar();
  const formContext = useForm<QConfigGeneratorCreationFormInput>({
    defaultValues: { chapterQuestionDistribution: {} },
    resolver: zodResolver(QConfigGeneratorCreationFormSchema),
  });

  const { data: diffTagsRes } = useGetTagsByCategoryQuery({
    variables: { input: { category: QuestionTagCategory.Difficulty } },
  });

  const { data: calcTagsRes } = useGetTagsByCategoryQuery({
    variables: { input: { category: QuestionTagCategory.Calculation } },
  });

  const { formState } = formContext;
  const { errors } = formState;
  useEffect(() => {
    if (errors.qbank) {
      setErrorMessage(typeof errors.qbank.message === 'string' ? errors.qbank.message : null);
    } else if (errors.chapterQuestionDistribution?.root) {
      setErrorMessage(
        typeof errors.chapterQuestionDistribution.root.message === 'string'
          ? errors.chapterQuestionDistribution.root.message
          : null,
      );
    } else if (errors.chapterQuestionDistribution) {
      setErrorMessage('กรุณาระบุจำนวนข้อสอบในแต่ละบทให้ครบถ้วน');
    } else {
      setErrorMessage(null);
    }
  }, [errors, setErrorMessage]);

  const difficultyMap = {
    hard: diffTagsRes?.qbsGetTagsByCategory.find((t) => t.name === 'ยาก')?.id ?? '',
    medium: diffTagsRes?.qbsGetTagsByCategory.find((t) => t.name === 'กลาง')?.id ?? '',
    easy: diffTagsRes?.qbsGetTagsByCategory.find((t) => t.name === 'ง่าย')?.id ?? '',
  };

  const calculationMap = {
    calculate: calcTagsRes?.qbsGetTagsByCategory.find((t) => t.name === 'คำนวณ')?.id ?? '',
    nonCalculate: calcTagsRes?.qbsGetTagsByCategory.find((t) => t.name === 'ไม่คำนวณ')?.id ?? '',
  };

  const [createQConfigGenerator] = useCreateQConfigGeneratorMutation({
    onCompleted: () => {
      showSnackbar({
        message: 'Starting QConfig generation. This may take a moment.',
        variant: 'success',
      });
      navigate(`/exam-courses/:courseId/qconfig-generators`, {
        params: { courseId: courseId },
      });
    },
    onError: () => {
      showSnackbar({
        message: 'Failed to create QConfig Generator. Please try again later.',
        variant: 'error',
      });
    },
  });

  const onSubmit = async (data: QConfigGeneratorCreationFormInput) => {
    const chapterDistributionTotal = data.chapterQuestionDistribution ?? {};

    await createQConfigGenerator({
      variables: {
        createQConfigGeneratorInput: {
          totalQuestions: data.questionBlueprint?.total ?? 0,
          needTotal: data.overview?.total ?? 0,
          name: data.overview?.name ?? '',
          generatorGroupLevel: QConfigGeneratorGroupLevel.Chapter,
          examCourseId: courseId,
          conditions: {
            tags: [
              {
                tagId: difficultyMap.hard,
                needTotal: data.questionBlueprint?.hard ?? 0,
              },
              {
                tagId: difficultyMap.medium,
                needTotal: data.questionBlueprint?.medium ?? 0,
              },
              {
                tagId: difficultyMap.easy,
                needTotal: data.questionBlueprint?.easy ?? 0,
              },
              {
                tagId: calculationMap.calculate,
                needTotal: data.questionBlueprint?.calculated ?? 0,
              },
              {
                tagId: calculationMap.nonCalculate,
                needTotal: data.questionBlueprint?.nonCalculated ?? 0,
              },
            ],
            questionTypes: [
              {
                type: QConfigQuestionType.Single,
                needTotal: data.questionBlueprint?.single ?? 0,
              },
              {
                type: QConfigQuestionType.ItemSet,
                needTotal: data.questionBlueprint?.itemSet ?? 0,
              },
            ],
            qBankStructureLevelItems: Object.entries(chapterDistributionTotal).map(
              ([levelItemId, needTotal]) => ({
                qBankStructureLevelItemId: levelItemId,
                needTotal,
              }),
            ),
          },
        },
      },
    });
  };

  return (
    <FormProvider {...formContext}>
      <Form onSubmit={formContext.handleSubmit(onSubmit)}>{children}</Form>
    </FormProvider>
  );
}
